const en = {
  // Navigation
  "nav": {
    "home": "Home",
    "approach": "Our approach",
    "offers": "Our offers",
    "segments": "Client segments",
    "calculator": "Carbon calculator",
    "blog": "Blog",
    "about": "About",
    "contact": "Contact"
  },
  
  // Home page
  "home": {
    "hero": {
      "title": "Decarbonization solutions adapted to your context",
      "subtitle": "We support your ecological transition with tailored solutions",
      "cta": "Discover our offers"
    },
    "models": {
      "title": "Innovative economic models",
      "subtitle": "Choose the formula that best suits your needs",
      "classic": {
        "title": "Classic Subscription",
        "description": "Full access to our platform with a monthly or annual subscription"
      },
      "performance": {
        "title": "Carbon Performance",
        "description": "Payment based on carbon emission reductions actually achieved"
      },
      "shared": {
        "title": "Shared Value",
        "description": "Sharing of financial savings generated by reducing your footprint"
      },
      "knowledge": {
        "title": "Knowledge Contribution",
        "description": "Free access in exchange for anonymized data to enrich our knowledge base"
      }
    },
    "benefits": {
      "title": "Why choose mypa?",
      "adapted": {
        "title": "Tailored solutions",
        "description": "Custom approaches to address your specific challenges"
      },
      "innovative": {
        "title": "Innovative models",
        "description": "Flexible formulas aligned with your objectives"
      },
      "expertise": {
        "title": "Recognized expertise",
        "description": "A team of carbon issue specialists"
      }
    }
  },
  
  // Approach page
  "approach": {
    "title": "Our approach",
    "subtitle": "A methodology adapted to your context",
    "methodology": {
      "title": "Methodology",
      "description": "Our approach combines technical expertise, field knowledge, and innovation to offer solutions truly adapted to your reality."
    },
    "steps": {
      "title": "Our process",
      "diagnostic": {
        "title": "Diagnostic",
        "description": "Complete analysis of your current situation and specific challenges"
      },
      "strategy": {
        "title": "Strategy",
        "description": "Development of a personalized roadmap with objectives and indicators"
      },
      "implementation": {
        "title": "Implementation",
        "description": "Support in deploying actions and solutions"
      },
      "monitoring": {
        "title": "Monitoring",
        "description": "Continuous measurement of results and adjustments to optimize impact"
      }
    }
  },
  
  // Offers page
  "offers": {
    "title": "Our offers",
    "subtitle": "Solutions adapted to each stage of your journey",
    "discovery": {
      "title": "Discovery",
      "description": "First step towards decarbonization with an initial diagnosis and recommendations",
      "features": [
        "Simplified carbon assessment",
        "Opportunity identification",
        "Priority recommendations",
        "Limited platform access"
      ]
    },
    "performance": {
      "title": "Performance",
      "description": "Comprehensive support to significantly reduce your footprint",
      "features": [
        "Detailed carbon assessment",
        "Personalized action plan",
        "Implementation support",
        "Full platform access",
        "Quarterly indicator monitoring"
      ]
    },
    "strategy": {
      "title": "Strategy",
      "description": "Deep transformation of your organization towards a low-carbon model",
      "features": [
        "Integrated carbon strategy",
        "Executive coaching",
        "Team training",
        "Integration into business strategy",
        "Reporting and communication",
        "Premium platform access"
      ]
    }
  },
  
  // Segments page
  "segments": {
    "title": "Client segments",
    "subtitle": "Solutions adapted to your profile",
    "intro": {
      "title": "Personalized client segmentation",
      "description": "We have identified different organization profiles with specific decarbonization needs."
    },
    "exporters": {
      "title": "Exporters",
      "description": "Solutions for exporting companies facing new regulations",
      "recommendation": {
        "title": "Our recommendation",
        "description": "Complete carbon assessment + Carbon Performance model",
        "details": "Ideal for companies affected by CBAM and wanting to secure their access to the European market."
      }
    },
    "large": {
      "title": "Large Companies",
      "description": "Strategic support for organizations with complex ESG issues",
      "recommendation": {
        "title": "Our recommendation",
        "description": "Strategy offer + Shared Value model",
        "details": "Perfect for companies with ambitious goals and wanting to integrate decarbonization into their overall strategy."
      }
    },
    "sme": {
      "title": "Committed SMEs",
      "description": "Accessible solutions for SMEs wishing to engage in a sustainable approach",
      "recommendation": {
        "title": "Our recommendation",
        "description": "Performance offer + Classic Subscription model",
        "details": "Adapted for companies with limited resources but wanting to structure their environmental approach."
      }
    },
    "vsmall": {
      "title": "VSEs/SMEs",
      "description": "Simplified and affordable approaches for smaller structures",
      "recommendation": {
        "title": "Our recommendation",
        "description": "Discovery offer + Knowledge Contribution model",
        "details": "Ideal for small structures wanting to initiate an environmental approach without significant investment."
      }
    }
  },
  
  // Calculator page
  "calculator": {
    "title": "Carbon footprint calculator",
    "subtitle": "Estimate your personal environmental impact",
    "intro": "Answer a few simple questions to get an estimate of your personal carbon footprint and tailored recommendations.",
    "select": "Select an option",
    "next": "Next",
    "previous": "Previous",
    "calculate": "Calculate my footprint",
    "steps": {
      "housing": "Housing",
      "transport": "Transport",
      "food": "Food",
      "consumption": "Consumption",
      "results": "Results"
    },
    "housing": {
      "title": "Your housing",
      "size": "What is the size of your home?",
      "size_small": "Small (< 50 m²)",
      "size_medium": "Medium (50-100 m²)",
      "size_large": "Large (> 100 m²)",
      "type": "What type of housing do you live in?",
      "type_apartment": "Apartment",
      "type_house": "House",
      "energy": "What is the approximate amount of your monthly electricity bill?",
      "energy_low": "Low (< $50)",
      "energy_medium": "Medium ($50-$100)",
      "energy_high": "High (> $100)",
      "heating": "What type of heating do you mainly use?",
      "heating_electric": "Electric",
      "heating_gas": "Gas",
      "heating_oil": "Oil",
      "heating_renewable": "Renewable energy"
    },
    "transport": {
      "title": "Your transportation",
      "car": "How many kilometers do you drive per month approximately?",
      "car_none": "No car",
      "car_low": "Few (< 500 km)",
      "car_medium": "Medium (500-1000 km)",
      "car_high": "Many (> 1000 km)",
      "fuel": "What type of fuel does your main vehicle use?",
      "fuel_gasoline": "Gasoline",
      "fuel_diesel": "Diesel",
      "fuel_hybrid": "Hybrid",
      "fuel_electric": "Electric",
      "public": "How often do you use public transportation?",
      "public_never": "Never",
      "public_sometimes": "Sometimes",
      "public_often": "Often",
      "plane": "How many flights do you take per year on average?",
      "plane_none": "None",
      "plane_few": "1-2 flights",
      "plane_many": "3 or more flights"
    },
    "food": {
      "title": "Your diet",
      "diet": "What diet do you mainly follow?",
      "diet_vegan": "Vegan",
      "diet_vegetarian": "Vegetarian",
      "diet_flexitarian": "Flexitarian",
      "diet_meat": "Daily meat",
      "local": "How often do you consume local and seasonal products?",
      "local_always": "Always",
      "local_sometimes": "Sometimes",
      "local_rarely": "Rarely",
      "waste": "How much food do you estimate you throw away each week?",
      "waste_none": "Almost none",
      "waste_little": "A little",
      "waste_much": "A lot"
    },
    "consumption": {
      "title": "Your consumption",
      "shopping": "How often do you buy new clothes?",
      "shopping_rarely": "Rarely",
      "shopping_sometimes": "Sometimes",
      "shopping_often": "Often",
      "electronic": "How often do you renew your electronic devices?",
      "electronic_rarely": "Rarely (> 5 years)",
      "electronic_sometimes": "Sometimes (3-5 years)",
      "electronic_often": "Often (< 3 years)",
      "recycling": "Do you sort your waste?",
      "recycling_always": "Always",
      "recycling_sometimes": "Sometimes",
      "recycling_never": "Never"
    },
    "results": {
      "title": "Your results",
      "footprint": "Your carbon footprint is estimated at",
      "average": "The national average is",
      "breakdown": "Breakdown of your footprint",
      "recommendations": "Personalized recommendations"
    },
    "recommendations": {
      "heating_change": "Consider switching to a more eco-friendly heating system",
      "reduce_electricity": "Reduce your electricity consumption by opting for more efficient appliances",
      "reduce_car": "Limit your car travel and favor alternatives",
      "limit_flights": "Reduce the number of flights you take each year",
      "use_public_transport": "Use public transportation more often",
      "reduce_meat": "Reduce your meat consumption",
      "local_food": "Favor local and seasonal products",
      "reduce_waste": "Reduce food waste",
      "responsible_consumption": "Adopt more responsible consumption",
      "extend_electronics": "Extend the lifespan of your electronic devices",
      "start_recycling": "Start sorting your waste",
      "continue_efforts": "Continue your efforts, you're on the right track!",
      "join_initiatives": "Join local initiatives to amplify your impact"
    }
  },
  
  // Blog page
  "blog": {
    "title": "Blog",
    "description": "News, analysis, and advice on decarbonization",
    "categories": {
      "all": "All articles",
      "regulation": "Regulation",
      "strategy": "Strategy",
      "financing": "Financing",
      "case_study": "Case studies",
      "innovation": "Innovation",
      "market": "Market"
    },
    "read_more": "Read more",
    "search": "Search for an article",
    "recent_posts": "Recent posts",
    "popular_tags": "Popular tags",
    "newsletter": {
      "title": "Subscribe to our newsletter",
      "description": "Receive our latest articles and news directly in your inbox",
      "placeholder": "Your email address",
      "button": "Subscribe"
    },
    "posts": {
      "cbam": {
        "title": "Impact of CBAM on exporting companies",
        "summary": "Analysis of the consequences of the carbon border adjustment mechanism for exporters",
        "content": {
          "p1": "The Carbon Border Adjustment Mechanism (CBAM) represents a major change for exporting companies. This new European regulation aims to level the playing field between EU producers subject to strict climate policies and importers from third countries where these constraints are less stringent.",
          "p2": "For exporting companies, CBAM implies new reporting obligations and potentially additional costs. The most affected sectors are steel, aluminum, cement, fertilizers, and electricity, but other sectors could be included in the future.",
          "p3": "Preparing for CBAM requires a thorough understanding of the carbon footprint of your products and your supply chain. Companies must implement reliable measurement and reporting systems to quantify their greenhouse gas emissions.",
          "p4": "Beyond compliance, CBAM can also represent an opportunity for companies that have already invested in low-carbon production processes. These companies will be able to benefit from a competitive advantage in European markets compared to competitors less advanced in their ecological transition.",
          "p5": "Mypa offers specific support for companies affected by CBAM, including regulatory impact assessment, implementation of compliant measurement systems, and development of adaptation strategies."
        }
      },
      "strategies": {
        "title": "Effective decarbonization strategies for SMEs",
        "summary": "Practical guide to implementing carbon footprint reduction actions adapted to SMEs",
        "content": {
          "p1": "Decarbonization represents a particular challenge for SMEs, which often have limited resources but face the same environmental issues as large companies. However, their agility can be a major asset in this transition.",
          "p2": "The first step is to carry out a simplified carbon assessment to identify the main sources of emissions. For most SMEs, these are generally concentrated on building energy consumption, business travel, and logistics.",
          "p3": "Quick-win actions can be implemented quickly: energy optimization of premises, sustainable mobility policy, waste reduction, or team awareness of eco-friendly practices. These measures often allow financial savings while reducing the carbon footprint.",
          "p4": "To go further, SMEs can consider more structural investments: energy renovation of buildings, installation of solar panels, renewal of the vehicle fleet towards electric models, or redesign of production processes to reduce inputs and waste.",
          "p5": "Employee engagement is a key success factor. By involving teams in the approach and valuing individual initiatives, SMEs can create positive momentum around their decarbonization strategy."
        }
      },
      "financing": {
        "title": "Financing climate projects: opportunities and mechanisms",
        "summary": "Overview of financing solutions available for decarbonization projects",
        "content": {
          "p1": "Financing is often a major obstacle to the implementation of ambitious decarbonization projects. However, many mechanisms exist to support companies in this transition, whether public aid, private financing, or innovative solutions.",
          "p2": "Public aid comes in different levels: national subsidies for ecological transition, regional programs to support green innovation, or European funding such as the Innovation Fund or the LIFE program. These mechanisms can cover a significant part of the initial investments.",
          "p3": "On the private financing side, there is a proliferation of banking offers dedicated to ecological transition, with preferential rates for projects with positive impact. Investment funds specialized in climate finance also represent a growing source of funding.",
          "p4": "Innovative mechanisms are also emerging, such as energy performance contracts, where the provider is paid based on the savings achieved, or green crowdfunding, which allows mobilizing citizen savings for ecological projects.",
          "p5": "Finally, the valuation of carbon emission reductions, via voluntary or regulated markets, can constitute a complementary source of income for certain projects. This approach allows monetizing environmental benefits and thus improving the profitability of low-carbon investments."
        }
      }
    }
  },
  
  // Footer
  "footer": {
    "company": "Company",
    "about": "About",
    "careers": "Careers",
    "contact": "Contact",
    "legal": "Legal notice",
    "privacy": "Privacy policy",
    "terms": "Terms of use",
    "cookies": "Cookie management",
    "newsletter": {
      "title": "Stay informed",
      "description": "Sign up for our newsletter to receive our latest news",
      "placeholder": "Your email",
      "button": "Subscribe"
    },
    "copyright": "© 2025 mypa.earth. All rights reserved.",
    "social": "Follow us"
  },
  
  // Contact page
  "contact": {
    "title": "Contact us",
    "subtitle": "We're here to answer your questions",
    "form": {
      "name": "Name",
      "email": "Email",
      "company": "Company",
      "subject": "Subject",
      "message": "Message",
      "submit": "Send",
      "success": "Your message has been sent successfully. We will get back to you shortly.",
      "error": "An error occurred. Please try again."
    },
    "info": {
      "title": "Contact information",
      "email": "Email",
      "phone": "Phone",
      "address": "Address"
    },
    "faq": {
      "title": "Frequently asked questions",
      "q1": "How to start a decarbonization approach?",
      "a1": "The first step is to conduct a carbon assessment to identify your main emission sources. We offer support adapted to your level of maturity.",
      "q2": "What are your rates?",
      "a2": "Our rates vary according to the economic model chosen and the level of support. Contact us for a personalized quote.",
      "q3": "How long does it take to see results?",
      "a3": "The first results are generally visible within 3 to 6 months after starting the approach, with quick-win actions. The complete transformation takes place over several years.",
      "q4": "Do you work with companies of all sizes?",
      "a4": "Yes, we have developed offers adapted to each organization profile, from VSEs to large companies."
    }
  },
  
  // About page
  "about": {
    "title": "About mypa",
    "subtitle": "Manifest Your Planet Action",
    "mission": {
      "title": "Our mission",
      "description": "Make decarbonization accessible to all organizations, regardless of their size or sector, by offering solutions adapted to their context and resources."
    },
    "vision": {
      "title": "Our vision",
      "description": "A world where every organization actively contributes to the fight against climate change, by integrating decarbonization into its strategy and operations."
    },
    "values": {
      "title": "Our values",
      "innovation": {
        "title": "Innovation",
        "description": "We constantly develop new approaches to make decarbonization more accessible and effective."
      },
      "impact": {
        "title": "Impact",
        "description": "We measure our success by the emission reductions we enable our clients to achieve."
      },
      "inclusion": {
        "title": "Inclusion",
        "description": "We believe that the ecological transition must be inclusive and benefit everyone."
      },
      "integrity": {
        "title": "Integrity",
        "description": "We act with transparency and honesty in all our interactions."
      }
    },
    "team": {
      "title": "Our team",
      "description": "A multidisciplinary team of experts passionate about ecological transition.",
      "members": {
        "member1": {
          "name": "Sarah Benali",
          "role": "Founder & CEO",
          "bio": "Engineer by training with 15 years of experience in sustainability consulting."
        },
        "member2": {
          "name": "Thomas Dubois",
          "role": "Technical Director",
          "bio": "Expert in information systems and environmental data analysis."
        },
        "member3": {
          "name": "Aïcha Koné",
          "role": "Client Manager",
          "bio": "Specialist in supporting organizations in their ecological transition."
        }
      }
    },
    "partners": {
      "title": "Our partners",
      "description": "We collaborate with a network of partners to offer comprehensive solutions to our clients."
    }
  },
  
  // 404 page
  "notFound": {
    "title": "Page not found",
    "description": "The page you are looking for does not exist or has been moved.",
    "button": "Back to home"
  }
};

export default en;
